/**
 * Mock - DeepDive AI: chats, streaming answers, credits.
 * Per-domain mutable state lives in ./db.ts (never in the shared mock/db.ts).
 *
 * Every endpoint passes `{ latency: false }` to `simulate`: the prototype's
 * chats were session state that drew instantly (creating a chat navigated in
 * the same tick, a star or rename was immediate), and an answer's only wait is
 * its own pacing — `THINK_MS`, then the per-unit cadence (./streaming.ts).
 * `?mock-error=<method>` still works for each.
 */
import type {
  ChatAnswerRequest,
  ChatApi,
  ChatAssistantMessage,
  ChatEvent,
  ChatSummary,
  ChatUserMessage,
  SourceCtx,
} from '@/data-sources/contract';

import { db } from '../db';
import { answerQuestion } from '../engine/answers';
import { chatIdFor, titleFor } from '../engine/chats';
import { REFERENCE_DATE } from '../engine/reference';
import { allRows } from '../engine/rows';
import { applySentimentOverrides } from '../engine/sentimentOverrides';
import { simulate } from '../scenarios';
import { toContractAnswer } from './convert';
import { chatDb, chats, findChat, nextId, type StoredChat } from './db';
import { STREAM_MS_PER_UNIT, SETTLE_MS, THINK_MS, pacer, splitUnits } from './streaming';

const iso = (ms: number) => new Date(ms).toISOString();

const summaryOf = (chat: StoredChat): ChatSummary => ({
  id: chat.id,
  title: chat.title,
  createdAt: iso(chat.createdAt),
  starred: chat.starred,
});

class ChatNotFoundError extends Error {
  constructor(chatId: string) {
    super(`No chat "${chatId}".`);
    this.name = 'ChatNotFoundError';
  }
}

function mustFind(chatId: string): StoredChat {
  const chat = findChat(chatId);
  if (!chat) throw new ChatNotFoundError(chatId);
  return chat;
}

/*
 * Every row the app reads, with Tune sentiment's saved corrections applied —
 * so an answer about sentiment agrees with the cards the correction already
 * changed (the prototype read them from `DashboardProvider`).
 */
const rows = () => applySentimentOverrides(allRows, db.sentimentOverrides);

type Prepared = {
  chat: StoredChat;
  question: string;
  mode: 'fast' | 'expert';
  attempt: number;
  userMessage?: ChatUserMessage;
  replaces?: string;
  /** Where the answer goes in `chat.messages` once it settles. */
  insertAt: number;
};

/** Persist the question (ask) or remove the answer being replaced (regenerate). */
function prepare(request: ChatAnswerRequest): Prepared | { error: string } {
  const chat = findChat(request.chatId);
  if (!chat) return { error: `No chat "${request.chatId}".` };

  if (request.kind === 'ask') {
    const userMessage: ChatUserMessage = { id: nextId(), role: 'user', text: request.question };
    chat.messages.push(userMessage);
    return {
      chat,
      question: request.question,
      mode: request.mode,
      attempt: 0,
      userMessage,
      insertAt: chat.messages.length,
    };
  }

  const index = chat.messages.findIndex(
    (m) => m.role === 'assistant' && m.id === request.messageId,
  );
  const meta = chatDb.meta.get(request.messageId);
  if (index < 0 || !meta)
    return { error: `No answer "${request.messageId}" in chat "${request.chatId}".` };
  chat.messages.splice(index, 1);
  return {
    chat,
    question: meta.question,
    mode: meta.mode,
    attempt: meta.attempt + 1,
    replaces: request.messageId,
    insertAt: index,
  };
}

/*
 * The stream. Order and pacing are the prototype's two timers plus its reveal:
 * the trace from the start, the first unit at THINK_MS + 45, unit k at
 * THINK_MS + k·45, and the chart, sources and follow-ups at
 * THINK_MS + units·45 + SETTLE_MS.
 */
async function* streamAnswer(
  request: ChatAnswerRequest,
  ctx: SourceCtx,
): AsyncGenerator<ChatEvent> {
  await simulate('streamAnswer', ctx.signal, { latency: false });

  const prepared = prepare(request);
  if ('error' in prepared) {
    yield { type: 'error', error: { code: 'not-found', message: prepared.error } };
    return;
  }
  const { chat, question, mode, attempt, userMessage, replaces } = prepared;
  const messageId = nextId();
  const answer = toContractAnswer(answerQuestion(question, mode, rows(), attempt));
  chatDb.meta.set(messageId, { question, mode, attempt });
  /* One credit per generated answer, spent when it is asked — a stopped answer still cost one. */
  chatDb.creditsSpent += 1;

  const settle = (message: ChatAssistantMessage) => {
    /* The chat may have been deleted mid-answer; then there is nowhere to put it. */
    if (!findChat(chat.id)) return;
    chat.messages.splice(Math.min(prepared.insertAt, chat.messages.length), 0, message);
  };

  yield {
    type: 'accepted',
    messageId,
    ...(userMessage && { userMessage }),
    ...(replaces !== undefined && { replaces }),
  };
  for (const step of answer.reasoning) yield { type: 'reasoning', step };

  const at = pacer(ctx.signal);
  const units = splitUnits(answer.text);
  const think = THINK_MS[mode];
  let phase: 'thinking' | 'streaming' | 'settled' = 'thinking';
  let arrived = '';

  try {
    await at(think);
    phase = 'streaming';
    yield { type: 'answering' };
    for (let k = 1; k <= units.length; k += 1) {
      await at(think + k * STREAM_MS_PER_UNIT);
      const delta = units[k - 1]!;
      arrived += delta;
      yield { type: 'text', delta };
    }
    await at(think + units.length * STREAM_MS_PER_UNIT + SETTLE_MS);

    const message: ChatAssistantMessage = {
      id: messageId,
      role: 'assistant',
      status: 'done',
      answer,
      feedback: null,
      mode,
    };
    settle(message);
    phase = 'settled';
    if (answer.chart) yield { type: 'chart', chart: answer.chart };
    yield { type: 'citations', citations: answer.citations };
    yield { type: 'follow-ups', followUps: answer.followUps };
    yield { type: 'done', message };
  } finally {
    /*
     * Stopped. During thinking the answer is dropped altogether — nothing had
     * been said, and a question with an empty reply under it is stranger than
     * a question alone. Mid-stream it keeps what had arrived.
     */
    if (phase === 'streaming') {
      settle({
        id: messageId,
        role: 'assistant',
        status: 'stopped',
        answer: { text: arrived, reasoning: answer.reasoning, citations: [], followUps: [] },
        feedback: null,
        mode,
      });
    }
  }
}

export const mockChatApi: ChatApi = {
  async listChats(ctx) {
    await simulate('listChats', ctx.signal, { latency: false });
    return { chats: chats().map(summaryOf), asOf: iso(REFERENCE_DATE.getTime()) };
  },

  async getChat({ chatId }, ctx) {
    await simulate('getChat', ctx.signal, { latency: false });
    const chat = findChat(chatId);
    return chat ? { ...summaryOf(chat), messages: [...chat.messages] } : null;
  },

  async createChat({ question }, ctx) {
    await simulate('createChat', ctx.signal, { latency: false });
    const title = titleFor(question);
    const id = chatIdFor(title, new Set(chats().map((chat) => chat.id)));
    chatDb.seq += 1;
    const chat: StoredChat = {
      id,
      title,
      createdAt: REFERENCE_DATE.getTime() + chatDb.seq * 1000,
      starred: false,
      messages: [],
    };
    chats().unshift(chat);
    return summaryOf(chat);
  },

  async renameChat({ chatId, title }, ctx) {
    await simulate('renameChat', ctx.signal, { latency: false });
    const chat = mustFind(chatId);
    chat.title = title;
    return summaryOf(chat);
  },

  async starChat({ chatId, starred }, ctx) {
    await simulate('starChat', ctx.signal, { latency: false });
    const chat = mustFind(chatId);
    chat.starred = starred;
    return summaryOf(chat);
  },

  async deleteChat({ chatId }, ctx) {
    await simulate('deleteChat', ctx.signal, { latency: false });
    const list = chats();
    const index = list.findIndex((chat) => chat.id === chatId);
    if (index >= 0) list.splice(index, 1);
  },

  async rateMessage({ chatId, messageId, feedback }, ctx) {
    await simulate('rateMessage', ctx.signal, { latency: false });
    const chat = mustFind(chatId);
    chat.messages = chat.messages.map((m) =>
      m.role === 'assistant' && m.id === messageId ? { ...m, feedback } : m,
    );
  },

  streamAnswer,
};
